'use client';

import { useCallback } from 'react';
import { useAppDispatch, store } from '@/store/store';
import { workflowEventReceived } from '@/store/slices/workflowProgressSlice';
import { phoneWorkflowEventReceived } from '@/store/slices/phoneWorkflowProgressSlice';
import {
  radarBulkWorkflowMapped,
  type RadarBulkUnlockState,
} from '@/store/slices/radarBulkUnlockSlice';
import { bulkBatchBackgroundClaimed, type BulkBatchesState } from '@/store/slices/bulkBatchesSlice';
import { refreshBulkBatches } from '@/hooks/useBulkBatchTracking';
import type { WorkflowEvent } from '@/lib/types/workflow.types';
import type { PhoneWorkflowEvent } from '@/lib/types/phone-workflow.types';

/** The `workflow.progress` payload fields this handler reads. */
export interface RadarBulkWorkflowEvent {
  workflowId?: unknown;
  event?: unknown;
  category?: unknown;
  bulkBatchId?: unknown;
  /** The waterfall's own verdict - "credit_exhausted" means it stopped early rather than genuinely finding nothing. */
  status?: unknown;
  /**
   * The originating request's own context, stamped onto every pushed event by
   * Workflow-Service (see its notification.client.ts). `personId` is what
   * makes per-row bulk tracking possible without a request per contact.
   */
  input?: { personId?: unknown; companyId?: unknown };
}

/**
 * Handles People Search bulk-unlock SSE events.
 *
 * A bulk batch fans out server-side into one workflow per contact, and each
 * of those pushes exactly ONE live event - its terminal `workflow.finished`
 * (Workflow-Service suppresses the intermediate provider.searching /
 * verification.checking pushes for bulk items, so a 1000-item batch can't
 * flood the single per-user stream). Every such event carries both the
 * batch's `bulkBatchId` and `input.personId`.
 *
 * That pair is everything this feature needs:
 *  - `bulkBatchId` says the event is ours, and ticks the batch's progress.
 *  - `input.personId` says WHICH row finished, so the event can be fed into
 *    the same workflowProgress/phoneWorkflowProgress state a single-click
 *    unlock uses and that row reveals its real value immediately - no GET per
 *    contact, no waiting for a refetch.
 *
 * Returns true to claim the event, which stops sidebar.tsx's generic
 * per-workflow path from also firing a `GET /api/workflows/:id` resync per
 * finish (50 requests for a 25-row batch, for state this already has).
 */
export function useRadarBulkUnlockEvents() {
  const dispatch = useAppDispatch();

  return useCallback(
    (p: RadarBulkWorkflowEvent): boolean => {
      // `bulkBatchId` marks this as a fan-out event rather than a single-click
      // unlock; `input.personId` says which of our contacts it belongs to.
      if (typeof p.bulkBatchId !== 'string' || !p.bulkBatchId) return false;

      const radar = (store.getState() as { radarBulkUnlock: RadarBulkUnlockState }).radarBulkUnlock;
      const personId = typeof p.input?.personId === 'string' ? p.input.personId : undefined;
      // Matched on the contact, not the batch id: the service enqueues a
      // batch's work before its create response returns, so the earliest
      // events arrive while the browser still has no batch id to compare
      // against. The contacts are known from the moment of the click.
      // Anything matching neither is someone else's run (a Workspace bulk
      // job, handled further down the chain).
      if (!personId) return false;
      const isBackgroundExport = !!radar.backgroundExportContactIds[personId];
      if (!radar.pendingContactIds[personId] && !isBackgroundExport) return false;

      const category: 'email' | 'phone' = p.category === 'phone' ? 'phone' : 'email';
      const workflowId = typeof p.workflowId === 'string' ? p.workflowId : undefined;

      // An All-tab export over 100 runs in the background, but the Search
      // Service creates its batches, so nothing else tells this client which
      // are background work. A kind's batch over 100 is shown by its size
      // and sends no per-contact events at all; one with 100 or fewer (say
      // only 60 need phone) does send them, but looks inline by size. The
      // first finish per such batch claims it for the notification panel, and
      // one list fetch brings its card in with the real total straight away
      // instead of at the batch's 5th completion (its first `bulk.progress`).
      if (isBackgroundExport) {
        const key = `${category}:${p.bulkBatchId}`;
        const bulkBatches = (store.getState() as { bulkBatches: BulkBatchesState }).bulkBatches;
        if (!bulkBatches.claimedBackground[key]) {
          dispatch(bulkBatchBackgroundClaimed({ batchId: p.bulkBatchId, category }));
          void refreshBulkBatches(dispatch);
        }
      }

      if (workflowId) {
        dispatch(
          radarBulkWorkflowMapped({
            personId,
            field: category,
            workflowId,
            creditExhausted: p.status === 'credit_exhausted',
          })
        );
      }

      // Fed into the same per-workflow state the single-click flow reads, so
      // the row's existing cell renders this result with no bulk-specific UI.
      if (category === 'phone') {
        dispatch(phoneWorkflowEventReceived(p as unknown as PhoneWorkflowEvent));
      } else {
        dispatch(workflowEventReceived(p as unknown as WorkflowEvent));
      }

      // No counter to bump: progress is derived from the per-contact state
      // this dispatch just wrote (see radarBulkBatchProgress), so a duplicate
      // or replayed event can't skew it and a late one still lands.
      return true;
    },
    [dispatch]
  );
}
